import type { BlockingIssue } from "./slotProjection";

/**
 * Links a projection issue back to the control it is about. The form and the
 * preview are two columns of the same section, so an operator reading "no
 * weekdays selected" on the right should be able to click straight to the
 * picker on the left.
 *
 * The anchors are `data-preview-anchor` attributes on the form controls; a DOM
 * lookup keeps the preview components from having to thread refs back through
 * AvailabilitySettingsPanel, which is shared with the property drawer.
 */
export type PreviewAnchor =
  | "scope"
  | "activeFlag"
  | "slotDuration"
  | "slotInterval"
  | "topupTravelTime"
  | "leadsPerSlot"
  | "generationHorizonDays"
  | "minNoticeHours"
  | "recurrence"
  | "window"
  | "users";

const ANCHOR_BY_ISSUE: Record<BlockingIssue["id"], PreviewAnchor> = {
  scope: "scope",
  inactive: "activeFlag",
  "window-missing": "window",
  "window-order": "window",
  "duration-missing": "slotDuration",
  "interval-missing": "slotInterval",
  "capacity-missing": "leadsPerSlot",
  "duration-overruns": "window",
  "interval-overruns": "slotInterval",
  "recurrence-missing": "recurrence",
  "days-missing": "recurrence",
  "users-missing": "users",
  "notice-exceeds-horizon": "minNoticeHours",
  // Nothing to fix — the link just points at the window it is talking about.
  "own-availability": "window",
};

export function anchorForIssue(id: BlockingIssue["id"]): PreviewAnchor {
  return ANCHOR_BY_ISSUE[id];
}

/**
 * Scrolls the anchored control into view and focuses it. Focus lands on the
 * first focusable descendant so this works for plain inputs and for the
 * button-group controls (Repeat, Days) alike.
 */
export function focusPreviewAnchor(anchor: PreviewAnchor): void {
  const host = document.querySelector<HTMLElement>(`[data-preview-anchor="${anchor}"]`);
  if (!host) return;
  host.scrollIntoView({ behavior: "smooth", block: "center" });
  const focusable = host.matches("input, select, textarea, button")
    ? host
    : host.querySelector<HTMLElement>("input, select, textarea, button, [tabindex]");
  focusable?.focus({ preventScroll: true });
  // A short highlight, so the eye finds the field the click was about. The class
  // is removed on the next frame batch rather than left on the node.
  host.classList.add("ring-2", "ring-bridge-accent/50", "rounded-md");
  window.setTimeout(() => {
    host.classList.remove("ring-2", "ring-bridge-accent/50", "rounded-md");
  }, 1200);
}
